import React, { useEffect, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";

import * as client from "../../../client";
import { useToast } from "../../../context-providers/Toast";
import { apiErrorDetail } from "../../../api/errors";
import {
  useSuggestEndorsementPremium,
  useUpdateEndorsementPremium,
} from "./data";
import styles from "./Endorsements.module.css";

interface EndorsementPremiumDialogProps {
  reportId: string;
  endorsement: client.EndorsementView | null;
  onHide: () => void;
}

/** Two-way premium editor: the full-term and remainder-of-policy (prorated)
 * amounts stay linked through the proration factor — editing either derives
 * the other. Both are stored. */
export const EndorsementPremiumDialog: React.FC<
  EndorsementPremiumDialogProps
> = ({ reportId, endorsement, onHide }) => {
  const toast = useToast();
  const update = useUpdateEndorsementPremium(reportId);

  const [term, setTerm] = useState<number | null>(null);
  const [prorated, setProrated] = useState<number | null>(null);
  const [factor, setFactor] = useState<number | null>(null);
  const [rateUsed, setRateUsed] = useState<number | null>(null);
  const [edited, setEdited] = useState(false);

  const endorsementId = endorsement?.id ?? null;
  const existing = endorsement?.premium_change ?? null;
  const suggestion = useSuggestEndorsementPremium(endorsementId);
  const suggested = suggestion.data ?? null;

  const applySuggestion = (change: client.EndorsementPremiumChange) => {
    setTerm(change.term_premium_delta);
    setProrated(change.prorated_premium_delta);
    setFactor(change.proration_factor);
    setRateUsed(change.rate_used);
    setEdited(false);
  };

  useEffect(() => {
    setTerm(existing?.term_premium_delta ?? null);
    setProrated(existing?.prorated_premium_delta ?? null);
    setFactor(existing?.proration_factor ?? null);
    setRateUsed(existing?.rate_used ?? null);
    setEdited(existing?.edited ?? false);
  }, [endorsementId, existing]);

  // With nothing saved yet, the fields start from the suggestion — for
  // account_rate the computed delta, for custom just the proration factor that
  // links the two amounts.
  useEffect(() => {
    if (existing === null && suggested !== null) {
      applySuggestion(suggested);
    }
  }, [existing, suggested]);

  const handleSuggest = () => {
    if (suggested !== null) {
      applySuggestion(suggested);
      return;
    }
    toast.show({
      severity: "warn",
      summary: "No suggestion available",
      detail:
        (suggestion.error && apiErrorDetail(suggestion.error)) ??
        "Could not compute a suggestion.",
      life: 5000,
    });
  };

  const handleTermChange = (value: number | null) => {
    setTerm(value);
    setEdited(true);
    if (value !== null && factor !== null) {
      setProrated(Math.round(value * factor));
    }
  };

  const handleProratedChange = (value: number | null) => {
    setProrated(value);
    setEdited(true);
    if (value !== null && factor !== null && factor > 0) {
      setTerm(Math.round(value / factor));
    }
  };

  const canSave = term !== null && prorated !== null && factor !== null;

  const handleSave = () => {
    if (!endorsement || term === null || prorated === null || factor === null) {
      return;
    }
    update.mutate(
      {
        endorsementId: endorsement.id,
        premiumChange: {
          term_premium_delta: term,
          prorated_premium_delta: prorated,
          // A hand-keyed amount was not produced by the suggested rate.
          rate_used: edited ? null : rateUsed,
          proration_factor: factor,
          edited,
        },
      },
      {
        onSuccess: onHide,
        onError: (err) => {
          toast.show({
            severity: "error",
            summary: "Could not save the premium change",
            detail: apiErrorDetail(err) ?? "Please try again.",
            life: 5000,
          });
        },
      }
    );
  };

  return (
    <Dialog
      header={
        endorsement
          ? `Premium change — endorsement #${endorsement.endorsement_number}`
          : "Premium change"
      }
      visible={endorsement !== null}
      onHide={onHide}
      draggable={false}
      style={{ width: "460px" }}
      footer={
        <div className={styles.footer}>
          <Button label="Cancel" severity="secondary" text onClick={onHide} />
          <Button
            label="Save"
            onClick={handleSave}
            disabled={!canSave}
            loading={update.isPending}
          />
        </div>
      }
    >
      {endorsement && (
        <div className={styles.form}>
          <Button
            label={
              endorsement.premium_basis === "account_rate"
                ? "Suggest from account rate"
                : "Compute proration from policy dates"
            }
            icon="pi pi-calculator"
            outlined
            size="small"
            onClick={handleSuggest}
            loading={suggestion.isLoading}
          />
          <div className={styles.fieldRow}>
            <div className={styles.field}>
              <span className={styles.label}>Full term</span>
              <InputNumber
                value={term}
                onValueChange={(e) => handleTermChange(e.value ?? null)}
                mode="currency"
                currency="USD"
                locale="en-US"
                maxFractionDigits={0}
                placeholder="$0"
              />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Remainder of policy</span>
              <InputNumber
                value={prorated}
                onValueChange={(e) => handleProratedChange(e.value ?? null)}
                mode="currency"
                currency="USD"
                locale="en-US"
                maxFractionDigits={0}
                placeholder="$0"
              />
            </div>
          </div>
          <span className={styles.premiumHint}>
            {factor !== null
              ? `Proration factor ${factor.toFixed(4)} (days remaining / days in term). Negative amounts are a return premium.`
              : "Use the suggestion to pull the proration factor from the policy's term dates, then adjust either amount."}
          </span>
        </div>
      )}
    </Dialog>
  );
};
